import { gql, useQuery } from '@apollo/client'

import { useGetNewestData } from '@/clients/utoniq-core'
import {
  FetchNewPortfolioItemsQuery,
  FetchNewPortfolioItemsQueryVariables,
  ItemType,
  PortfolioItemsConnectionFragmentDoc,
  PortfolioItemsDetailFragmentDoc,
  PortfolioItemsQuery,
  PortfolioItemsQueryVariables,
} from '@/clients/utoniq-core/schema'
import { spreadPortfoliosBySku } from '@/core/sku'

export type State = ReturnType<typeof useViewModel>['state']
export type Action = ReturnType<typeof useViewModel>['actions']
type FilterType = ItemType | 'general'

export const useViewModel = () => {
  const { isFirstLoadRef } = useGetNewestData<
    FetchNewPortfolioItemsQuery,
    FetchNewPortfolioItemsQueryVariables
  >({
    query: FETCH_NEW_QUERY,
    variables: { pageInput: { limit: 24 } },
    identify: { __typename: 'User', id: data => data?.user?.id },
    fields: [
      {
        fieldName: 'organizationPortfolios',
        responsePath: 'user.organizationPortfolios',
        fragment: PortfolioItemsDetailFragmentDoc,
      },
    ],
  })

  const { data, loading, fetchMore, refetch } = useQuery<
    PortfolioItemsQuery,
    PortfolioItemsQueryVariables
  >(QUERY, {
    variables: { pageInput: { limit: 24 } },
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      isFirstLoadRef.current = false
    },
  })

  return {
    state: {
      loading,
      hasNextPage: !!data?.user?.organizationPortfolios?.pageInfo?.hasNextPage,
      organizationPortfolios: spreadPortfoliosBySku(
        data?.user?.organizationPortfolios ?? null
      ),
      filter: 'general' as FilterType,
    },
    actions: {
      onSort(key: number) {},
      async onFilterResale(resaleEnabled: boolean) {
        refetch({
          option: resaleEnabled
            ? {
                resaleEnabled,
              }
            : null,
        })
      },
      async onFilter(filter: ItemType | 'general') {
        refetch({
          type: filter === 'general' ? null : filter,
          option: null,
        })
      },
      async onFetchMore() {
        await fetchMore({
          variables: {
            pageInput: {
              limit: 15,
              cursor: data?.user?.organizationPortfolios?.pageInfo.endCursor,
            },
          },
        })
      },
    },
  }
}

const QUERY = gql`
  query PortfolioItems(
    $type: ItemType
    $pageInput: PageInput
    $option: PortfoliosOptionInput
  ) {
    user {
      id
      organizationPortfolios(
        type: $type
        pageInput: $pageInput
        option: $option
      ) {
        ...PortfolioItemsConnection
      }
    }
  }
  ${PortfolioItemsConnectionFragmentDoc}
`

const FETCH_NEW_QUERY = gql`
  query FetchNewPortfolioItems($type: ItemType, $pageInput: PageInput) {
    user {
      id
      organizationPortfolios(type: $type, pageInput: $pageInput) {
        ...PortfolioItemsConnection
      }
    }
  }
  ${PortfolioItemsConnectionFragmentDoc}
`
